import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import WheelOfLife from "@/components/WheelOfLife";
import ScoreBadge from "@/components/ScoreBadge";
import EmptyState from "@/components/EmptyState";
import InsightsList from "@/components/InsightsList";
import FindingsCards from "@/components/FindingsCards";
import PhysicalHabitCard from "@/components/PhysicalHabitCard";
import TimeRangeToggle from "@/components/TimeRangeToggle";
import {
  BAND_COLORS, DIMENSION_LABELS, DIMENSION_ORDER,
  bandFor, formatScore, contextLabelFor, formatDelta,
} from "@/lib/scoring";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { todayLocalISO } from "@/lib/calendar";
import { formatAxisDateAR, formatTooltipDateAR } from "@/lib/dates";

export default function Dashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("weekly");
  const [range, setRange] = useState("30");
  const [insights, setInsights] = useState([]);
  const [insightsMeta, setInsightsMeta] = useState({ learning: false, message: null });
  const [findings, setFindings] = useState([]);
  const [physicalHabit, setPhysicalHabit] = useState(null);

  useEffect(() => {
    let alive = true;
    const today = todayLocalISO();
    api.get("/dashboard/summary", { params: { today } }).then(({ data }) => {
      if (alive) setSummary(data);
    }).finally(() => alive && setLoading(false));
    api.get("/dashboard/physical", { params: { today, window: 30 } }).then(({ data }) => {
      if (alive) setPhysicalHabit(data);
    }).catch(() => alive && setPhysicalHabit(null));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    const today = todayLocalISO();
    Promise.all([
      api.get("/dashboard/insights", { params: { days: range, today } }),
      api.get("/dashboard/findings", { params: { days: range, today } }),
    ]).then(([i, f]) => {
      if (!alive) return;
      setInsights(i.data.insights || []);
      setInsightsMeta({ learning: !!i.data.learning, message: i.data.message });
      setFindings(f.data.findings || []);
    });
    return () => { alive = false; };
  }, [range]);

  if (loading) return <div className="py-16 text-center text-ink-600">Cargando…</div>;
  if (!summary) return null;

  const dims = summary.dimensions[view] || {};
  const overallByView = summary.overall[view];

  if (summary.logs_count === 0) {
    return (
      <div className="space-y-6 animate-fade-up" data-testid="dashboard-empty">
        <header>
          <p className="tk-label">Tu bienestar</p>
          <h1 className="font-outfit text-3xl text-ink-900 mt-1">Dashboard</h1>
        </header>
        <EmptyState
          title="No tenemos suficiente información todavía"
          description="Completá tus primeros registros diarios para descubrir tus patrones y empezar a ver tu rueda de bienestar."
          action={
            <button onClick={() => navigate("/log")} className="tk-btn-primary" data-testid="dashboard-cta-first-log">
              Hacer mi primer registro
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="tk-label">Tu bienestar</p>
          <h1 className="font-outfit text-3xl text-ink-900 mt-1">Dashboard</h1>
        </div>
        <TimeRangeToggle value={range} onChange={setRange} testId="dashboard-range" />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="tk-card lg:col-span-2" data-testid="dashboard-wheel-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <p className="tk-label">Rueda de la Vida</p>
              <h3 className="font-outfit text-xl text-ink-900 mt-1">
                {view === "daily" ? "Hoy" : view === "weekly" ? "Esta semana" : "Este mes"}
              </h3>
            </div>
            <div className="inline-flex rounded-full bg-sand-100 p-1" data-testid="dashboard-view-toggle">
              {[
                { id: "daily", label: "Hoy" },
                { id: "weekly", label: "Semana" },
                { id: "monthly", label: "Mes" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setView(opt.id)}
                  className={`px-3 py-1 rounded-full text-xs font-dmsans transition ${
                    view === opt.id ? "bg-white text-ink-900 shadow-sm" : "text-ink-600"
                  }`}
                  data-testid={`dashboard-view-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <WheelOfLife scores={dims} size={360} />
          <div className="grid grid-cols-5 gap-2 mt-2">
            {DIMENSION_ORDER.map((d) => {
              const v = dims[d];
              const band = bandFor(v);
              return (
                <div key={d} className="text-center" data-testid={`dashboard-dim-${d}`}>
                  <div className="mx-auto h-2 w-2 rounded-full mb-1" style={{ background: BAND_COLORS[band] }} />
                  <p className="tk-label text-[10px]">{DIMENSION_LABELS[d]}</p>
                  <p className="font-outfit text-sm text-ink-900 mt-0.5">{formatScore(v)}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="tk-card" data-testid="dashboard-headline-card">
            <p className="tk-label">Score actual</p>
            <div className="flex items-baseline gap-3 mt-2">
              <span className="font-outfit text-5xl text-ink-900" data-testid="dashboard-headline-score">
                {formatScore(overallByView)}
              </span>
              <span className="font-dmsans text-sm text-ink-600">/10</span>
            </div>
            <p
              className="font-outfit text-sm mt-1"
              style={{ color: BAND_COLORS[bandFor(overallByView)] }}
              data-testid="dashboard-context-label"
            >
              {contextLabelFor(overallByView)}
            </p>
            <DeltaPill delta={summary.overall.weekly_delta} prev={summary.overall.weekly_prev} />
          </div>

          <div className="tk-soft-card">
            <p className="tk-label">Tu progreso</p>
            <div className="space-y-3 mt-3">
              <SummaryRow label="Hoy" score={summary.overall.daily} />
              <SummaryRow label="Semana" score={summary.overall.weekly} />
              <SummaryRow label="Mes" score={summary.overall.monthly} />
            </div>
            <button
              onClick={() => navigate("/correlations")}
              className="tk-btn-ghost w-full mt-4"
              data-testid="dashboard-go-correlations"
            >
              Ver correlaciones →
            </button>
          </div>
        </div>
      </div>

      {/* Findings */}
      {findings.length > 0 && (
        <section className="space-y-3" data-testid="dashboard-findings-section">
          <div className="flex items-baseline justify-between">
            <h2 className="font-outfit text-xl text-ink-900">Hallazgos</h2>
            <p className="tk-label">Periodo seleccionado</p>
          </div>
          <FindingsCards items={findings} />
        </section>
      )}

      {/* Physical habit (holistic) */}
      {physicalHabit && <PhysicalHabitCard data={physicalHabit} />}

      {/* Trend */}
      <div className="tk-card" data-testid="dashboard-trend-card">
        <p className="tk-label">Tendencia general (30 días)</p>
        <div className="h-56 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={summary.series} margin={{ top: 10, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid stroke="#F0EBE0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6B7A75" }} tickFormatter={formatAxisDateAR} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: "#6B7A75" }} />
              <Tooltip
                contentStyle={{ background: "#fff", borderRadius: 12, border: "1px solid #E6E2D8" }}
                labelFormatter={formatTooltipDateAR}
                formatter={(v) => [v == null ? "—" : Number(v).toFixed(1), "Score"]}
              />
              <Line
                type="monotone"
                dataKey="overall"
                stroke="#2A9D8F"
                strokeWidth={2}
                dot={{ r: 3, fill: "#2A9D8F" }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insights */}
      <section className="space-y-3" data-testid="dashboard-insights-section">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h2 className="font-outfit text-xl text-ink-900">Lo que estamos descubriendo</h2>
          <p className="tk-label">Periodo seleccionado</p>
        </div>
        <InsightsList
          items={insights}
          learning={insightsMeta.learning}
          message={insightsMeta.message}
          emptyHint="Sumá más registros en este período para que podamos analizar tus patrones."
        />
      </section>
    </div>
  );
}

function SummaryRow({ label, score }) {
  const band = bandFor(score);
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: BAND_COLORS[band] }} />
        <p className="font-dmsans text-sm text-ink-900">{label}</p>
      </div>
      <p className="font-outfit text-lg text-ink-900">{formatScore(score)}</p>
    </div>
  );
}

function DeltaPill({ delta, prev }) {
  if (delta == null) {
    return (
      <p className="font-dmsans text-xs text-ink-600 mt-2" data-testid="dashboard-delta-empty">
        Sumá registros la próxima semana para ver tu tendencia.
      </p>
    );
  }
  const isUp = delta > 0.05;
  const isDown = delta < -0.05;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const color = isUp ? "#2A9D8F" : isDown ? "#E07A5F" : "#6B7A75";
  return (
    <div className="flex items-center gap-2 mt-3" data-testid="dashboard-delta">
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-dmsans"
        style={{ background: `${color}18`, color }}
      >
        <Icon size={12} />
        {formatDelta(delta)} vs semana anterior
      </span>
      {prev != null && (
        <span className="font-dmsans text-[11px] text-ink-600">prev. {Number(prev).toFixed(1)}</span>
      )}
    </div>
  );
}
